import React, { useCallback, useEffect, useMemo, useState } from "react";
import { StyleProp, StyleSheet, View, ViewStyle } from "react-native";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { LayoutChangeEvent } from "react-native/Libraries/Types/CoreEventTypes";

const ANIMATION_DURATION = 300;

type ToggleViewProps = {
    isOpen?: boolean;
    style?: StyleProp<ViewStyle>;
    children?: React.ReactNode;
};

const ToggleView = ({ isOpen, style, children }: ToggleViewProps) => {
    const styles = useStyles();
    const height = useSharedValue(0);
    const [maxHeight, setMaxHeight] = useState(0);
    useEffect(() => {
        height.value = withTiming(isOpen ? maxHeight : 0, {
            duration: ANIMATION_DURATION,
        });
    }, [height, isOpen, maxHeight]);

    const onLayout = useCallback((event: LayoutChangeEvent) => {
        const { height: h } = event.nativeEvent.layout;
        setMaxHeight(h);
    }, []);

    const animatedStyles = useAnimatedStyle(() => ({
        height: height.value,
    }));
    return (
        <Animated.View style={[styles.animatedView, style, animatedStyles]}>
            <View style={styles.innerView} onLayout={onLayout}>
                {children}
            </View>
        </Animated.View>
    );
};

const useStyles = () => {
    return useMemo(
        () =>
            StyleSheet.create({
                animatedView: {
                    overflow: "hidden",
                    position: "relative",
                },
                innerView: {
                    flex: 1,
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                },
            }),
        [],
    );
};

export default ToggleView;
